import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { setNavigate } from '@/main/routing.utilities';
import { cleanupBroadcastChannels } from '@/services/broadcastChannel.utilities';
import { cancelRequestsOnUnload } from '@/requests/pendingRequests.utilities';
import { cleanupOnUnload } from '@/main/cleanup.utilities';
import { addEventListeners, onSystemMessage } from '@/services/notifier.service';
import { setSystemMessage } from '@/systemWarning/systemWarning.actions';
import { subscribeToAuthChangeChannel } from '@/homescreen/homescreen.actions';
import { fetchConfiguration } from '@/services/systemConfiguration.utilities';
import { Tracker } from '@/track/Tracker.atom';
import { LoadingFallback } from './LoadingFallback.atom';
import { setAIAssistantDisplayed } from '@/aiAssistant/aiAssistant.actions';
import { getDefaultDisplayed } from '@/aiAssistant/aiAssistant.utilities';
import { init18n } from '@/utilities/i18n.utilities';

/**
 * Wrapper component that wraps all the Route components.
 * Everything that has to happen once per application load and applies to every single component should happen here.
 */
export const Main: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);

  useEffect(() => {
    fetchConfiguration().then(() => {
      init18n()
        .then(() => {
          setIsI18nInitialized(true);
        })
        .catch((error) => {
          console.error('Error initializing i18n: ', error);
        });

      setConfigLoaded(true);
      setAIAssistantDisplayed(getDefaultDisplayed(), true);
    });
    subscribeToAuthChangeChannel();
    onSystemMessage(setSystemMessage);
    addEventListeners();

    // we need to expose the navigate function to call it from services
    setNavigate(navigate);
    const beforeUnloadListener = () => cancelRequestsOnUnload();
    window.addEventListener('beforeunload', beforeUnloadListener);
    return () => {
      cleanupBroadcastChannels();
      cancelRequestsOnUnload();
      cleanupOnUnload();
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, []);

  if (!configLoaded || !isI18nInitialized) {
    return <LoadingFallback />;
  }

  return (
    <>
      <Tracker />
      <Outlet />
    </>
  );
};
